/**
 * Talk to ChopLink from the web app (epic 2026-09-25-chopbook-company-lists,
 * 00 D4, "Match with LinkedIn"). ChopLink opens LinkedIn pages in the
 * recruiter's own browser; the web app cannot. Its manifest lets exactly this
 * stage's web app message it (`externally_connectable`), and its service
 * worker answers `ping` and `matchCompany` (`background/company-match.ts`).
 *
 * `chrome.runtime.sendMessage` exists on the page only when some installed
 * extension lists this origin, so "no ChopLink" shows up as either no
 * `chrome.runtime` at all or a `lastError`.
 */
import { getCwsExtensionId, resolveStage } from "@nu-assist/shared-fe";

export type ChopLinkMatch =
  | { status: "matched"; linkedinId: string; name: string | null; logo: boolean }
  | { status: "redirected"; linkedinId: string; observedSlug: string }
  | { status: "not_found" }
  /** `retryable` is absent from ChopLink builds older than 2026-10-01. */
  | { status: "failed"; detail: string; retryable?: boolean; sessionExpired?: boolean }
  | { status: "refused"; detail: string };

export interface ChopLink {
  /** Null when ChopLink is not installed in this browser (or does not answer). */
  ping(): Promise<{ lookupPermission: boolean } | null>;
  matchCompany(slug: string): Promise<ChopLinkMatch>;
}

/** A LinkedIn lookup opens a tab and waits on it: up to ~50 s on a slow page. */
const MATCH_TIMEOUT_MS = 90_000;
const PING_TIMEOUT_MS = 3_000;

interface RuntimeLike {
  sendMessage(extensionId: string, message: unknown, callback: (response: unknown) => void): void;
  lastError?: { message?: string };
}

function runtime(): RuntimeLike | null {
  const chromeGlobal = (globalThis as { chrome?: { runtime?: RuntimeLike } }).chrome;
  return typeof chromeGlobal?.runtime?.sendMessage === "function" ? chromeGlobal.runtime : null;
}

function send(extensionId: string, message: unknown, timeoutMs: number): Promise<unknown> {
  const rt = runtime();
  if (!rt) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    try {
      rt.sendMessage(extensionId, message, (response) => {
        clearTimeout(timer);
        // Reading lastError is what tells Chrome the failure was handled.
        resolve(rt.lastError ? null : response);
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

/** ChopLink's id on this stage, or null where there is no ChopLink build. */
export function chopLinkExtensionId(): string | null {
  return getCwsExtensionId(resolveStage(), "baby-chops");
}

export function createChopLink(extensionId: string | null = chopLinkExtensionId()): ChopLink {
  return {
    async ping() {
      if (!extensionId) return null;
      const res = (await send(extensionId, { type: "ping" }, PING_TIMEOUT_MS)) as {
        ok?: boolean;
        lookupPermission?: boolean;
      } | null;
      return res?.ok ? { lookupPermission: !!res.lookupPermission } : null;
    },
    async matchCompany(slug) {
      if (!extensionId) return { status: "failed", detail: "ChopLink is not available" };
      const res = (await send(
        extensionId,
        { type: "matchCompany", slug },
        MATCH_TIMEOUT_MS
      )) as ChopLinkMatch | null;
      return res?.status ? res : { status: "failed", detail: "ChopLink did not answer" };
    },
  };
}
